import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { GET_CLIMBERS_PROGRESS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import type { ClimberProgress } from "../types/mmp-graphql";

interface GetClimbersProgressResponse {
  getClimbersProgress: (ClimberProgress | null)[];
}

// getClimbersProgress queries each climber's prg- records server-side and caps a
// request at 100 climbers.
const CHUNK_SIZE = 100;

/**
 * Batch-loads progress for a list of climbers — used by teacher roster and
 * climber-detail views. Product-agnostic: the server reads every prg- record a
 * climber has and merges them, so a coach whose active product differs from the
 * climber's still sees their progress.
 *
 * Returns a map of climberId → ClimberProgress. Climbers with no progress
 * record yet (lazy creation) are absent from the map.
 */
export const useClimbersProgress = (climberIds: string[]) => {
  // Sorted so a reordered roster hits the same cache entry instead of refetching.
  const sortedIds = [...climberIds].sort();
  const climberIdsKey = sortedIds.join(",");

  // NOTE: the query caches a plain ARRAY (not a Map). React Query persistence
  // (persistQueryClient -> localStorage) JSON-serializes the cache, and a Map
  // serializes to "{}" and rehydrates as a non-Map plain object (breaking
  // `.get`). The Map is rebuilt client-side from the serializable array below.
  const { data, error, isLoading } = useQuery<ClimberProgress[]>({
    queryKey: ["climbersProgress", climberIdsKey],
    queryFn: async () => {
      const records: ClimberProgress[] = [];

      for (let i = 0; i < sortedIds.length; i += CHUNK_SIZE) {
        const resp = await graphQLClient.request<GetClimbersProgressResponse>(
          GET_CLIMBERS_PROGRESS,
          { climberIds: sortedIds.slice(i, i + CHUNK_SIZE) },
        );
        // Climbers with no progress record come back as null; keep the records
        // that resolved.
        (resp.getClimbersProgress ?? []).forEach((progress) => {
          if (progress?.parentID) records.push(progress);
        });
      }

      return records;
    },
    enabled: sortedIds.length > 0,
  });

  useReactQueryErrorReporter("Could not load climber progress", error, false);

  // Key by the record's own parentID (the climberId).
  const progressByClimberId = useMemo(() => {
    const map = new Map<string, ClimberProgress>();
    (data ?? []).forEach((progress) => {
      if (progress.parentID) map.set(progress.parentID, progress);
    });
    return map;
  }, [data]);

  return { progressByClimberId, loading: isLoading, error };
};

/**
 * A single climber's merged progress record — the climber-detail and report
 * views. Shares `useClimbersProgress`'s cache entry, so the several consumers on
 * one page issue one request between them rather than one each.
 */
export const useClimberProgress = (climberId: string | undefined) => {
  const climberIds = useMemo(() => (climberId ? [climberId] : []), [climberId]);
  const { progressByClimberId, loading, error } =
    useClimbersProgress(climberIds);

  return {
    progress: climberId ? progressByClimberId.get(climberId) : undefined,
    loading,
    error,
  };
};
